/**
 * SiteCardReportRag — the per-report-type RAG dot strip on a dashboard
 * site card (DC2).
 *
 * Six tiny dots, one per report type, in the platform's natural workflow
 * order: Feasibility → Environmental → Power-availability → Water-resource
 * → Interconnection → Test-fit. For each type:
 *
 *   ● filled  = a report of that type exists for this site; the dot takes
 *               that report's `overallStatus` colour (green/amber/red).
 *               When several versions exist, the latest (by `createdAt`)
 *               wins — same rule the auto-rerun detector uses.
 *   ○ outline = no report of that type yet; muted "not yet evaluated".
 *
 * Each dot gets a single-letter type code (F/E/P/W/R/I) underneath and a
 * tooltip: `{label}: {status} — {recommendation OR 'not yet generated'}`.
 *
 * Pure / prop-driven so it unit-tests in isolation: it takes the site and
 * the reports list (the caller already joins `siteRag.reports` against
 * `site.reportIds`). Legacy site docs without the newer fields are a
 * non-event — the only thing this reads off the site is `reportIds`.
 */
import React from 'react';
import type { SiteEntity } from '../../types/site';
import type { SiteRagReport, RagStatus, ReportOverallStatus } from '../../types/siteRag';
import { REPORT_TYPE_REGISTRY, type ReportTypeId } from '../../config/reportTypes';
import { REPORT_SURFACE_ORDER } from '../../config/reportPresentation';
import { getDisplayVerdict } from '../../lib/reportVerdict';
import { ReportVerdictDot } from '../shared/ReportVerdictDot';

interface Props {
  site: SiteEntity;
  /** Reports already loaded in the store. The component joins these
   *  against `site.reportIds` itself (an empty list ⇒ all dots empty). */
  reports: SiteRagReport[];
  /** Optional click handler — receives the report id when a filled dot is
   *  clicked, the report type when an empty dot is clicked. The card wires
   *  this to navigation; absent ⇒ the strip is purely informational. */
  onSelectType?: (arg: { reportType: ReportTypeId; reportId?: string }) => void;
}

/**
 * Platform-natural workflow order. Mirrors `RECOMMENDED_REPORT_ORDER`
 * used by the next-action heuristic (DC4) so the strip and the hint
 * agree on "which type comes next".
 */
export const SITE_CARD_REPORT_ORDER: readonly ReportTypeId[] = [
  ...REPORT_SURFACE_ORDER,
];

/** Single-letter code per type for the strip. `R` for water-resource so it
 *  doesn't collide with `W`-for-… nothing, but the convention in the pack's
 *  mock was `W`; we keep `W` for water and `T` for test-fit, `I` for
 *  interconnection, `P` for power. */
export const SITE_CARD_TYPE_CODE: Record<ReportTypeId, string> = {
  feasibility: 'F',
  environmental: 'E',
  'power-availability': 'P',
  'water-resource': 'W',
  interconnection: 'I',
  'supply-chain-risk': 'C',
  'test-fit': 'T',
  'executive-summary': 'X',
  // PSR-1 (row 0.99290) — Public Sentiment Report. 'S' for Sentiment.
  'public-sentiment': 'S',
  'custom-brief': 'B',
  // FIN-1 (wf w7nuyquwd) — Financial Viability Report. '$' for Financial.
  financial: '$',
};

const STATUS_LABEL: Record<ReportOverallStatus, string> = {
  green: 'Green',
  amber: 'Amber',
  red: 'Red',
  withheld: 'Decision pending',
};

/**
 * Latest report (by `createdAt`) for each type that this site has, keyed
 * by `ReportTypeId`. Reports with no `reportType` are treated as
 * `feasibility` — the historical default before typed reports shipped.
 */
export function latestReportByType(site: SiteEntity, reports: SiteRagReport[]): Map<ReportTypeId, SiteRagReport> {
  const ids = new Set(site.reportIds ?? []);
  const out = new Map<ReportTypeId, SiteRagReport>();
  for (const r of reports) {
    if (!ids.has(r.id)) continue;
    const t = (r.reportType ?? 'feasibility') as ReportTypeId;
    const prev = out.get(t);
    if (!prev || new Date(r.createdAt).getTime() > new Date(prev.createdAt).getTime()) {
      out.set(t, r);
    }
  }
  return out;
}

export function SiteCardReportRag({ site, reports, onSelectType }: Props) {
  const byType = latestReportByType(site, reports);

  return (
    <div
      className="flex items-center gap-1"
      data-testid={`site-card-rag-${site.id}`}
      aria-label="Reports by type"
    >
      {SITE_CARD_REPORT_ORDER.map(typeId => {
        const def = REPORT_TYPE_REGISTRY[typeId];
        const code = SITE_CARD_TYPE_CODE[typeId];
        const report = byType.get(typeId);
        const status = report?.overallStatus;
        const verdict = report ? getDisplayVerdict(report) : null;
        const recommendation = report?.conclusion?.recommendation;
        const tooltip = report
          ? `${def.shortLabel}: ${
              verdict?.band == null
                ? verdict?.bandLabel ?? 'Insufficient data'
                : verdict?.label && verdict.label !== '—'
                ? verdict.label
                : status
                ? STATUS_LABEL[status]
                : 'pending'
            } — ${recommendation ?? 'no recommendation yet'}`
          : `${def.shortLabel}: not yet generated`;
        const codeClass = report ? 'text-text-secondary' : 'text-text-disabled';
        const interactive = Boolean(onSelectType);

        const dot = (
          <ReportVerdictDot
            report={report ?? null}
            size="xs"
            tooltip={tooltip}
            data-testid={`site-card-rag-dot-${site.id}-${typeId}`}
          />
        );

        const body = (
          <span className="flex flex-col items-center gap-px leading-none">
            {dot}
            <span className={`type-meta font-bold ${codeClass}`}>{code}</span>
          </span>
        );

        return interactive ? (
          <button
            key={typeId}
            type="button"
            title={tooltip}
            aria-label={tooltip}
            data-testid={`site-card-rag-btn-${site.id}-${typeId}`}
            className="px-px focus:outline-none focus-visible:ring-1 focus-visible:ring-primary"
            onClick={(e) => {
              e.stopPropagation();
              onSelectType?.({ reportType: typeId, reportId: report?.id });
            }}
          >
            {body}
          </button>
        ) : (
          <span key={typeId} title={tooltip} aria-label={tooltip} className="px-px">
            {body}
          </span>
        );
      })}
    </div>
  );
}
